/**
 * `/changes` - the newest DOE documents in this archive, by DOE's own issue
 * date (BL-0129). A catalog search, nothing stored.
 *
 * Server-rendered from data the server fetched, the FamilyTimeline way: a
 * real addon route with `asyncConnect`, so a crawler that never runs the
 * bundle still gets every document and every link.
 */
import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { UniversalLink } from '@plone/volto/components';
import { asyncConnect } from '@plone/volto/helpers/AsyncConnect';
import { Helmet, flattenToAppURL } from '@plone/volto/helpers';

import { getChanges } from '../../actions/changes';
import { useSiteConfig } from '../../helpers/useSiteConfig';
import { PUBLISHER_NAME } from '../../siteConfig/branding';
import {
  CHANGES_PATH,
  changesDataFeedJsonLd,
  FEED_PATH,
  datedItems,
  fmtDate,
  groupByDate,
  labelOf,
} from './describe';

export const Changes = ({ changes, loading }) => {
  const site = useSiteConfig();
  const origin = (site.baseUrl || '').replace(/\/+$/, '');
  const canonical = `${origin}${CHANGES_PATH}`;
  const groups = groupByDate(datedItems(changes));
  const jsonld = changesDataFeedJsonLd(site, changes);

  const heading = 'What changed at DOE';
  const description =
    `The newest DOE documents in the ${site.title}, ` +
    `by the date DOE issued them.`;

  return (
    <div className="pp-changes" id="view">
      <Helmet>
        <title>{`${heading} | ${site.seoName}`}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <link
          rel="alternate"
          type="application/rss+xml"
          title={`${heading} - ${site.title}`}
          href={`${origin}${FEED_PATH}`}
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={heading} />
        <meta property="og:description" content={description} />
        <meta property="og:site_name" content={PUBLISHER_NAME} />
        <meta property="og:url" content={canonical} />
        {jsonld && (
          <script type="application/ld+json">{JSON.stringify(jsonld)}</script>
        )}
      </Helmet>

      <div className="prc-kit__main">
        <article className="prc-kit__doc">
          <h1 className="prc-kit__doc-h1">{heading}</h1>
          <p className="pp-changes__lede">
            {description} <a href={FEED_PATH}>RSS feed</a>
          </p>

          {groups.length > 0 ? (
            groups.map((group) => (
              <section className="pp-changes__day" key={group.date}>
                <h2 className="pp-changes__date">
                  Issued {fmtDate(group.date)}
                </h2>
                <ul className="pp-changes__list">
                  {group.items.map((item) => (
                    <li className="pp-changes__item" key={item['@id']}>
                      <UniversalLink
                        href={flattenToAppURL(item['@id'])}
                        className="pp-changes__doc"
                      >
                        {labelOf(item)}
                      </UniversalLink>
                    </li>
                  ))}
                </ul>
              </section>
            ))
          ) : (
            <p className="pp-changes__empty">
              {loading ? 'Loading documents...' : 'No documents yet.'}
            </p>
          )}
        </article>
      </div>
    </div>
  );
};

export default compose(
  asyncConnect([
    {
      key: 'changes',
      promise: ({ store: { dispatch } }) => dispatch(getChanges()),
    },
  ]),
  connect((state) => ({
    changes: state.changes?.data,
    loading: state.changes?.loading,
  })),
)(Changes);
